import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, db, functions } from '../firebase.config';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { setDoc, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const AuthContext = createContext();

export function AuthContextProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const ipRecordAttemptedForUid = useRef(null);

  async function signUp(email, password, additionalData) {
    // 1) Create the Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const createdUser = userCredential.user;

    // 2) Optionally set the displayName in Auth profile
    if (additionalData.displayName) {
      await updateProfile(createdUser, { displayName: additionalData.displayName });
    }

    // 3) Write initial user doc (fires addUserDefaultFields trigger)
    await setDoc(doc(db, 'Users', createdUser.uid), {
      uid: createdUser.uid,
      firstName: additionalData.firstName,
      lastName: additionalData.lastName,
      email: createdUser.email,
      createdAt: new Date(), 
    });
    try {
      await httpsCallable(functions, 'sendVerificationEmail')({});
      return { verificationEmailSent: true };
    } catch (error) {
      console.error('Verification email could not be sent; retry from Account.', error.code);
      return { verificationEmailSent: false };
    }

  }

  async function logIn(email, password) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    setUser(userCredential.user);
  }

  async function logOut() {
    await signOut(auth);
    setUser(null);
  }

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!active) return;
      setAuthLoading(true);
      setUser(currentUser);
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'Users', currentUser.uid);
          const [idTokenResult, userDoc] = await Promise.all([
            // Firebase refreshes expired tokens itself. Forcing a refresh here adds
            // a network round trip to every protected route cold load.
            currentUser.getIdTokenResult(),
            getDoc(userDocRef),
          ]);
          if (!active) return;
          const profile = userDoc.exists() ? userDoc.data() : null;
          setUserProfile(profile);
          setIsAdmin(Boolean(idTokenResult?.claims?.admin));
          setAuthLoading(false);
          window.performance?.mark?.('bubble-auth-ready');

          const shouldRecordIp = !profile?.IP_Address
            && ipRecordAttemptedForUid.current !== currentUser.uid;
          if (shouldRecordIp) {
            // Analytics bookkeeping must never hold up account rendering.
            ipRecordAttemptedForUid.current = currentUser.uid;
            const recordIP = httpsCallable(functions, 'recordIP');
            recordIP().catch((err) => console.error('IP recording failed', err));
          }
        } catch (error) {
          if (!active) return;
          console.error('Error loading auth/profile state:', error);
          setUserProfile(null);
          setIsAdmin(false);
          setAuthLoading(false);
        }
      } else {
        setUserProfile(null);
        setIsAdmin(false);
        ipRecordAttemptedForUid.current = null;
        setAuthLoading(false);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        signUp,
        logIn,
        logOut,
        user,
        userProfile,
        hasUsedTrial: Boolean(userProfile?.hasUsedTrial || userProfile?.freeTrialComplete),
        isAdmin,
        authLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function UserAuth() {
  return useContext(AuthContext);
}
